import { nanoid } from '@reduxjs/toolkit';
import {transform} from 'ol/proj';
import { AmbientLight, MeshBasicMaterial, Scene, SphereGeometry, Vector3 } from 'three';
import { toast } from '../../../app';
import { t } from '../../../localization';
import { Projection } from '../../../redux/projections-slice';
import {
  clearAerialTooltip,
  clearViewerTooltip,
  setAerialTooltip,
  setViewerTooltip
} from "../../viewer";
import { CustomMouseEvent, PanoControls } from "../controls";
import { Viewer, ViewerProps } from '../main';
import {
  closestUtmZone,
  DataProjectionCoordinate,
  estimateTransform,
  latitudeLongitude,
  reprojectPoints,
  SceneCoordinate
} from '../projections';
import { ChangeDetector, getPointScale } from "../utilities";
import { aerialObsColor, pointObsColor, PointObservation } from './observations';
import {Vector as VectorSource} from "ol/source";
import {Vector as VectorLayer} from "ol/layer";
import {Circle as CircleStyle, Fill, Stroke, Style} from "ol/style";
import {Point} from "ol/geom";
import {Feature} from "ol";
import * as olColor from 'ol/color';

export enum LocalObservationTypes {
  None,
  Scene,
  Aerial
}

export interface LocalObservation {
  id: string
  scene: DataProjectionCoordinate
  aerial: number[]
  error: number
  active: LocalObservationTypes
}

export class LocalAligner {
  public scene: Scene;
  private mapLayer: VectorLayer<any>;
  private mapSource: VectorSource<any>;
  private controls: PanoControls;

  private setObservations: React.Dispatch<any[]>;

  public enabled = false;
  private activeObservation: LocalObservation = null;
  private observations = [] as LocalObservation[];
  private sphereGeometry = new SphereGeometry(0.05, 20, 20);

  constructor(controls: PanoControls, props: ViewerProps) {
    this.controls = controls;
    this.setObservations = props.setLocalObservations;

    this.initScene();
    this.addMapLayers();
  }

  get viewer(): Viewer {
    return this.controls.viewer;
  }

  get map() {
    return this.viewer.minimap.map;
  }

  get aerialTooltip() {
    return [
      t('local-projection.click_to_add_aerial_observation'),
      t('local-projection.right-click_to_cancel')
    ];
  }

  get sceneTooltip() {
    return [
      t('local-projection.click_to_add_point_observation'),
      t('local-projection.right-click_to_cancel')
    ];
  }

  get isSceneObs() {
    return this.activeObservation
      ? this.activeObservation.active === LocalObservationTypes.Scene
      : false;
  }

  get isAerialObs() {
    return this.activeObservation
      ? this.activeObservation.active === LocalObservationTypes.Aerial
      : false;
  }

  initScene() {
    this.scene = new Scene();
    this.scene.add(new AmbientLight(0xFFFFFF));
  }

  addMapLayers() {
    const styleColor = `#${aerialObsColor.toString(16)}`;
    const color = olColor
      .asArray(styleColor)
      .slice();
    color[3] = 0.8;

    this.mapSource = new VectorSource({
      features: [],
      wrapX: false
    });

    this.mapLayer = new VectorLayer({
      zIndex: 1,
      source: this.mapSource,
      style: new Style({
        image: new CircleStyle({
          radius: 5,
          stroke: new Stroke({
            color: 'rgba(0, 0, 0, 0.7)'
          }),
          fill: new Fill({
            color: color
          })
        })
      })
    });
  }

  async getLocalProjection(estimator: string, units: string) {
    const filled = this.observations.filter(observation => {
      return observation.scene && observation.aerial;
    });

    const valid = (this.observations.length >= 3)
      && (filled.length === this.observations.length);

    if (!valid) {
      toast.error(t('local-projection.all_observations_must_be_used_to_calculate_projection'));
      return;
    }

    const input = this.observations.map(observation => {
      return observation.scene.toArray().slice(0,2);
    });

    const latlon = this.observations.map(observation => {
      return transform(observation.aerial, 'EPSG:3857', 'EPSG:4326');
    });

    // Calculate average map coordinate
    const average = [
      latlon.map(p => p[0]).reduce((a,b) => a+b, 0) / latlon.length,
      latlon.map(p => p[1]).reduce((a,b) => a+b, 0) / latlon.length
    ];

    // Determine closest UTM zone and reproject points
    const {projection, utmZone} = closestUtmZone(new Vector3(...average), units);
    const output = await reprojectPoints(latlon, latitudeLongitude, projection);

    // Estimate local transform
    const local = estimateTransform(estimator, input, output);
    if (!local.transform) return;

    const newProjection = {
      ...projection,
      transform: local.transform
    } as Projection;

    // Update observation errors
    this.observations.forEach((observation, index) => {
      observation.error = local.errors[index];
    });

    this.refreshObservationList();

    return {projection: newProjection, utmZone};
  };

  resetErrorValues() {
    this.observations.forEach(observation => {
      observation.error = null;
    });
  }

  setActiveObservation(id: string, type: LocalObservationTypes) {
    this.observations = this.observations.map(observation => {
      return {
        ...observation,
        active: observation.id === id ? type : LocalObservationTypes.None,
      };
    });

    this.activeObservation = this.observations.find(x => x.id === id);
    this.refreshObservationList();
    this.drawObservations();
  }

  addPointObservationMesh(position) {
    let sphereMaterial = new MeshBasicMaterial({color: pointObsColor});
    let mesh = new PointObservation(this.sphereGeometry, sphereMaterial);

    this.scene.add(mesh);

    mesh.position.copy(position);
    this.setPointScale(mesh);

    return mesh;
  }

  drawObservations() {
    if (!this.enabled) return;
    this.drawPointObservations();
    this.drawAerialObservations();
  }

  /** @private */
  drawPointObservations() {
    // Delete old point meshes
    this.scene.children
      .filter(mesh => mesh instanceof PointObservation)
      .forEach(mesh => this.scene.remove(mesh));

    // Draw new point meshes
    this.observations.forEach(observation => {
      if (!observation.scene) return;

      // Active observation will be ignored, to allow for replacing of points
      if (this.activeObservation) {
        const isActiveObservation = observation.id === this.activeObservation.id;
        if (isActiveObservation && this.isSceneObs) return;
      }

      // Add point
      const position = new DataProjectionCoordinate(observation.scene).toScene();
      const mesh = this.addPointObservationMesh(position);
      this.scene.add(mesh);
    });
  }

  /** @private */
  drawAerialObservations() {
    // Delete old points
    this.mapSource.clear();

    let features = [];
    this.observations.forEach(observation => {
      if (!observation.aerial) return;

      // Active observation will be ignored, to allow for replacing of points
      if (this.activeObservation) {
        const isActiveObservation = observation.id === this.activeObservation.id;
        if (isActiveObservation && this.isAerialObs) return;
      }

      const polygon = new Point(observation.aerial);
      const feature = new Feature(polygon);
      features.push(feature);
    });

    this.mapSource.addFeatures(features);
  }

  addNewObservations(numObservations) {
    for (let i=0; i<numObservations; i++) {
      this.observations.push({
        id: nanoid(),
        scene: null,
        aerial: null,
        error: null,
        active: LocalObservationTypes.None
      });
    }

    this.resetErrorValues();
    this.refreshObservationList();
  }

  getCoordinate(event) : Vector3 {
    return this.controls.getPickerResults(event).coordinate;
  }

  setPointScale(mesh) {
    const scale = getPointScale(this.viewer.camera, mesh);
    mesh.scale.set(scale, scale, scale);
    mesh.geometry.computeBoundingSphere();
  }

  refreshObservationList() {
    this.setObservations([...this.observations]);
  }

  setState(state) {
    this.resetObservations();
    this.enabled = state;

    if (state) {
      this.map.addLayer(this.mapLayer);
    } else {
      this.map.removeLayer(this.mapLayer);
    }
  }

  getByID(id) {
    return this.observations.find(x => x.id === id);
  }

  removeByID(id) {
    const observation = this.getByID(id);
    if (!observation) return;

    this.observations = this.observations.filter(x => x.id !== id);
    this.resetErrorValues();
    this.clearActiveObservation();
    this.drawObservations();
  }

  resetObservations() {
    this.observations = [];
    this.clearActiveObservation();
    this.drawObservations();
  }

  clearActiveObservation() {
    clearAerialTooltip();
    clearViewerTooltip();
    this.setActiveObservation(null, LocalObservationTypes.None);
  }

  onMouseUp(event: CustomMouseEvent): boolean {
    if (!this.enabled || !this.activeObservation || event.mouseMoved) {
      return false;
    }

    if (event.isLeftClick && this.isSceneObs) {
      this.onSceneClick(event);
      return true;
    } else if (event.isRightClick) {
      this.clearActiveObservation();
      return true;
    }

    return false;
  }

  onSceneClick(event) {
    let coordinate = this.getCoordinate(event);
    if (!coordinate) return;

    toast.success(t('local-projection.new_point_observation_added'));
    let position = new SceneCoordinate(coordinate).toDataProjection();
    this.activeObservation.scene = position;
    this.clearActiveObservation();
    this.drawObservations();
  }

  onMapClick(event) {
    if (!this.enabled || !this.isAerialObs) {
      return false;
    }

    toast.success(t('local-projection.new_aerial_observation_added'));
    this.activeObservation.aerial = event.coordinate;
    this.clearActiveObservation();
  }

  onMouseMoveScene() {
    if (!this.enabled || !this.isSceneObs) {
      return false;
    }

    if (this.activeObservation) {
      setViewerTooltip(this.sceneTooltip);
    } else {
      clearViewerTooltip();
    }
  }

  onMouseMoveAerial() {
    if (!this.enabled || !this.isAerialObs) {
      return false;
    }

    if (this.activeObservation) {
      setAerialTooltip(this.aerialTooltip);
    } else {
      clearAerialTooltip();
    }
  }

  updateAllMeshes() {
    this.scene.children.forEach(child => {
      if (child instanceof PointObservation) {
        this.setPointScale(child);
      }
    });
  }

  update(changeDetector: ChangeDetector) {
    if (!this.enabled || !changeDetector.changed) return;

    this.updateAllMeshes();
  }
}